import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaCopy, FaDownload, FaSyncAlt, FaFileContract } from 'react-icons/fa';
import { useQuotesStore } from '../quotesStore';
import { quotesService } from '../quotesService';
import ssoService from '../../Auth/ssoService';
import {
  QUOTE_TYPE_OPTIONS,
  DISTRIBUTION_CHANNEL_OPTIONS,
  QUOTE_STATUS_OPTIONS,
  POLICY_STATE_OPTIONS,
  INCLUDE_LMS_OPTIONS,
} from '../constants';
import {
  getDiscretionPairErrors,
  validateSingleDiscretion,
} from '../utils/calculations';
import { Badge } from '../../../components/Badge';
import { Tooltip } from '../../../components/Tooltip';
import { Textarea } from '../../../components/forms/Textarea';
import { Select } from '../../../components/forms/Select';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { cn } from '../../../utils/cn';
import { getDownloadFileName } from '../../../utils/download';
import { formatUsd } from '../utils/money';
import { costsFromQuoteOutput } from '../../PolicyAdmin/utils';
import type { QuoteOutput, ReinsurancePartner } from '../types';

/** Above this count, desktop uses horizontal scroll instead of squeezing all columns into the viewport. */
const QUOTE_OUTPUTS_MATRIX_SCROLL_OVER_VARIATIONS = 12;

export const QuoteDetailComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentQuote: quote,
    currentQuoteError: error,
    currentQuoteLoading: loading,
    fetchQuoteDetail,
    updateQuoteProgress,
    addNote,
    updateQuote,
    deleteQuote,
    updateOutputField,
    patchQuoteOutput,
  } = useQuotesStore();

  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState<string>('');
  const statusSelectRef = useRef<HTMLSelectElement>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [successModal, setSuccessModal] = useState<{ isOpen: boolean; message: string; quoteId?: number; analysisFileUrl?: string }>({
    isOpen: false,
    message: '',
  });
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: false,
    policy: false,
    census: false,
    insurance: false,
    handbook: false,
    outputs: true,
    notes: false,
    metadata: false,
  });
  
  const [editableOutputs, setEditableOutputs] = useState<Record<number, Record<string, string>>>({});
  const [outputFieldErrors, setOutputFieldErrors] = useState<Record<number, Record<string, string>>>({});
  const [selectedVariations, setSelectedVariations] = useState<Set<number>>(new Set());
  const [reinsurancePartners, setReinsurancePartners] = useState<ReinsurancePartner[]>([]);

  useEffect(() => {
    quotesService.getReinsurancePartners().then((partners) => {
      setReinsurancePartners(partners.filter((p) => p.is_active));
    }).catch(() => setReinsurancePartners([]));
  }, []);
  const [isProcessingNewVersion, setIsProcessingNewVersion] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const analysisPollingRef = useRef<NodeJS.Timeout | null>(null);
  const analysisPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const analysisSurfacedRef = useRef(false);
  const POLLING_INTERVAL = 10000;
  // Analysis + PowerPoint run as a background job that can take several minutes
  // (many sequential Graph calls + a chained PowerPoint step). Poll every 5s and
  // give the job a generous deadline so a slow-but-working run isn't reported as
  // a failure to the user.
  const ANALYSIS_POLL_INTERVAL_MS = 5000;
  const ANALYSIS_GENERATION_DEADLINE_MS = 20 * 60 * 1000;
  const [isGenerating, setIsGenerating] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const wasGeneratingRef = useRef(false);
  const [isSyncingPowerPoint, setIsSyncingPowerPoint] = useState(false);
  const powerPointSyncPollingRef = useRef<NodeJS.Timeout | null>(null);
  const powerPointSyncPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [syncFeedback, setSyncFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const [soldPlanConfirm, setSoldPlanConfirm] = useState<{ outputId: number; variationIndex: number } | null>(null);
  const [isMarkingSold, setIsMarkingSold] = useState(false);
  const [isSimpleView, setIsSimpleView] = useState(true);

  const censusIgnoredReasonLabels = {
    state_not_allowed: 'State is not one of the 50 states or Puerto Rico',
    annual_salary_empty: 'Annual salary is empty',
    annual_salary_lt_15000: 'Annual salary is less than 15000',
    age_out_of_range: 'Age is outside supported range',
    birth_date_empty: 'Birth date is empty',
    employed_date_empty: 'Employed date is empty',
    under_18_at_effective_date: 'Under 18 as of the policy effective date',
  } as const;

  useEffect(() => {
    if (!syncFeedback) return;

    const timeout = setTimeout(() => {
      setSyncFeedback(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [syncFeedback]);

  useEffect(() => {
    if (!statusNotice) return;

    const timeout = setTimeout(() => {
      setStatusNotice(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [statusNotice]);

  useEffect(() => {
    if (id) {
      fetchQuoteDetail(Number(id), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Item 38: a failed generation run is terminal — never treat it as
    // "still generating" (which would spin forever). The BE also resets
    // expected_outputs_count to 0 on failure; this guard is belt-and-suspenders.
    if (
      quote &&
      !quote.generation_failed &&
      quote.expected_outputs_count &&
      quote.expected_outputs_count > 0 &&
      !quote.all_outputs_generated
    ) {
      setIsGenerating(true);
      setShowProgress(true);
      wasGeneratingRef.current = true;
      startPolling();
    } else {
      setIsGenerating(false);
      stopPolling();
      
      if (quote?.all_outputs_generated && wasGeneratingRef.current) {
        const timer = setTimeout(() => {
          setShowProgress(false);
        }, 3000);
        
        return () => {
          clearTimeout(timer);
          stopPolling();
        };
      } else {
        setShowProgress(false);
      }
    }

    return () => {
      stopPolling();
      stopAnalysisPolling();
      stopPowerPointSyncPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.expected_outputs_count, quote?.all_outputs_generated, quote?.generation_failed]);

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      if (id) {
        try {
          const response = await quotesService.getQuoteDetail(Number(id));
          updateQuoteProgress(Number(id), {
            expected_outputs_count: response.expected_outputs_count,
            outputs_count: response.outputs_count,
            all_outputs_generated: response.all_outputs_generated,
            outputs_progress: response.outputs_progress,
            outputs: response.outputs,
            generation_failed: response.generation_failed,
            generation_error: response.generation_error,
          });
        } catch {
          // Silently handle polling errors
        }
      }
    }, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (quote) {
      setStatusValue(quote.status);
    }
  }, [quote]);

  useEffect(() => {
    if (quote?.outputs) {
      const initial: Record<number, Record<string, string>> = {};
      const initialErrors: Record<number, Record<string, string>> = {};
      quote.outputs.forEach((output) => {
        const ud = formatDecimalToPercentage(output.underwriting_discretion);
        const bd = formatDecimalToPercentage(output.business_discretion);
        const sasBd = formatDecimalToPercentage(output.business_discretion_percentage_for_sas);
        const lmsBd = formatDecimalToPercentage(output.business_discretion_percentage_for_lms);
        initial[output.id] = {
          underwriting_discretion: ud,
          business_discretion: bd,
          business_discretion_percentage_for_sas: sasBd,
          business_discretion_percentage_for_lms: lmsBd,
        };

        // Validate the saved values up-front so red borders appear for any
        // discretion that was persisted before/around the validation rule.
        const rowErrors: Record<string, string> = {};
        const pairErrors = getDiscretionPairErrors(ud, bd);
        if (pairErrors.underwriting_discretion) {
          rowErrors.underwriting_discretion = pairErrors.underwriting_discretion;
        }
        if (pairErrors.business_discretion) {
          rowErrors.business_discretion = pairErrors.business_discretion;
        }
        const sasError = validateSingleDiscretion(sasBd);
        if (sasError) {
          rowErrors.business_discretion_percentage_for_sas = sasError;
        }
        const lmsError = validateSingleDiscretion(lmsBd, 'LMS');
        if (lmsError) {
          rowErrors.business_discretion_percentage_for_lms = lmsError;
        }
        if (Object.keys(rowErrors).length > 0) {
          initialErrors[output.id] = rowErrors;
        }
      });
      // Merge instead of replace so any in-flight edits to fields not seeded
      // here survive the reseed that fires whenever quote.outputs reference
      // changes (optimistic updates, polling).
      setEditableOutputs((prev) => {
        const next: Record<number, Record<string, string>> = { ...prev };
        Object.keys(initial).forEach((idKey) => {
          const id = Number(idKey);
          next[id] = { ...prev[id], ...initial[id] };
        });
        return next;
      });
      setOutputFieldErrors(initialErrors);
    }
  }, [quote?.outputs]);

  // Seed selectedVariations only when the quote ID changes (i.e., on first
  // load of a quote). Re-running on every quote.outputs reference change
  // would clobber the user's per-row checkbox edits every time the BE
  // returns an updated output (e.g., after toggling Primary / Sold).
  //
  // Seed from the persisted is_selected_for_analysis flag so the selection
  // used to generate the analysis survives a reload. Treat undefined as
  // selected (the field defaults to true server-side).
  useEffect(() => {
    if (quote?.outputs) {
      setSelectedVariations(
        new Set(
          quote.outputs
            .filter((o) => o.is_selected_for_analysis !== false)
            .map((o) => o.id)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);

  const matrixBottomScrollRef = useRef<HTMLDivElement>(null);
  const matrixTopScrollRef = useRef<HTMLDivElement>(null);
  const matrixScrollSpacerRef = useRef<HTMLDivElement>(null);

  const syncMatrixScrollSpacerWidth = useCallback(() => {
    const bottom = matrixBottomScrollRef.current;
    const spacer = matrixScrollSpacerRef.current;
    if (bottom && spacer) {
      spacer.style.width = `${bottom.scrollWidth}px`;
    }
  }, []);

  useLayoutEffect(() => {
    syncMatrixScrollSpacerWidth();
    const bottom = matrixBottomScrollRef.current;
    if (!bottom || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      syncMatrixScrollSpacerWidth();
    });
    ro.observe(bottom);
    return () => ro.disconnect();
  }, [quote?.outputs, syncMatrixScrollSpacerWidth]);

  const handleMatrixTopScroll = () => {
    const top = matrixTopScrollRef.current;
    const bottom = matrixBottomScrollRef.current;
    if (!top || !bottom || bottom.scrollLeft === top.scrollLeft) return;
    bottom.scrollLeft = top.scrollLeft;
  };

  const handleMatrixBottomScroll = () => {
    const top = matrixTopScrollRef.current;
    const bottom = matrixBottomScrollRef.current;
    if (!top || !bottom || top.scrollLeft === bottom.scrollLeft) return;
    top.scrollLeft = bottom.scrollLeft;
  };

  const handleAddNote = async () => {
    if (!quote || !noteText.trim()) return;

    try {
      setAddingNote(true);
      await addNote(quote.id, { notes: noteText });
      setNoteText('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  const handleStatusEdit = () => {
    if (!quote) return;
    setStatusValue(quote.status);
    setIsEditingStatus(true);
    setTimeout(() => {
      statusSelectRef.current?.focus();
    }, 0);
  };

  const handleStatusBlur = async () => {
    if (!quote) return;
    
    if (statusValue !== quote.status) {
      if (statusValue === 'closed_won') {
        const hasSoldPlan = quote.outputs?.some((o) => o.is_sold_plan);
        if (!hasSoldPlan) {
          setSuccessModal({ isOpen: true, message: 'Please select a sold plan before marking this quote as Closed Won.' });
          setStatusValue(quote.status);
          setIsEditingStatus(false);
          return;
        }
      }
      try {
        await updateQuote(quote.id, { status: statusValue as typeof quote.status });
        if (statusValue === 'closed_won') {
          setStatusNotice('Census records are being persisted in background.');
        }
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to update status');
        setStatusValue(quote.status);
      }
    }
    
    setIsEditingStatus(false);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusValue(newStatus);
  };

  const handleDeleteClick = () => {
    setDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quote) return;
    
    try {
      await deleteQuote(quote.id);
      navigate('/quotes');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete quote');
      setDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(false);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatUser = (user: { first_name: string; last_name: string }): string => {
    return `${user.first_name} ${user.last_name}`;
  };

  const formatCurrency = formatUsd;

  const getFileNameFromUrl = (fileUrl: string): string =>
    getDownloadFileName(fileUrl, null, 'generated-presentation.pptx');

  const getStatusVariant = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'approved':
      case 'closed_won':
        return 'success';
      case 'pricing_review_needed':
        return 'warning';
      case 'expired_outdated_census':
      case 'closed_lost':
        return 'error';
      case 'proposal_submitted':
        return 'info';
      default:
        return 'default';
    }
  };

  const downloadCensusFile = async () => {
    if (!quote?.census_file) return;
    
    try {
      const fileUrl = quote.census_file.startsWith('http://') || quote.census_file.startsWith('https://')
        ? quote.census_file
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.census_file}`;
      
      const token = ssoService.getAccessToken();
      const response = await fetch(fileUrl, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      const blob = await response.blob();
      const filename = getDownloadFileName(
        quote.census_file,
        quote.census_file_name,
        'census_file.xlsx',
        '.xlsx',
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      const fileUrl = quote.census_file.startsWith('http://') || quote.census_file.startsWith('https://')
        ? quote.census_file
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.census_file}`;
      window.open(fileUrl, '_blank');
    }
  };

  const downloadAnalysisFile = async () => {
    if (!quote?.analysis_file || !quote?.is_analysis_file_generated) return;

    try {
      const fileUrl = quote.analysis_file.startsWith('http://') || quote.analysis_file.startsWith('https://')
        ? quote.analysis_file
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.analysis_file}`;

      const token = ssoService.getAccessToken();
      const response = await fetch(fileUrl, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const filename = getDownloadFileName(quote.analysis_file, null, 'analysis_file.xlsx', '.xlsx');

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      const fileUrl = quote.analysis_file.startsWith('http://') || quote.analysis_file.startsWith('https://')
        ? quote.analysis_file
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.analysis_file}`;
      window.open(fileUrl, '_blank');
    }
  };

  const downloadPowerPointPresentation = async () => {
    if (!quote?.id) return;

    try {
      const { blob, filename } = await quotesService.downloadPowerPointPresentation(quote.id);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: unknown) {
      const fallbackMessage =
        error instanceof Error ? error.message : 'Failed to download PowerPoint presentation';
      setSyncFeedback({ type: 'error', message: fallbackMessage });
    }
  };

  const parseErrorMessage = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object' && 'response' in error) {
      const response = error as {
        response?: { data?: { detail?: string; error?: string; message?: string } };
      };
      return (
        response.response?.data?.detail ||
        response.response?.data?.error ||
        response.response?.data?.message ||
        fallback
      );
    }
    return error instanceof Error ? error.message : fallback;
  };

  const stopPowerPointSyncPolling = () => {
    if (powerPointSyncPollingRef.current) {
      clearInterval(powerPointSyncPollingRef.current);
      powerPointSyncPollingRef.current = null;
    }
    if (powerPointSyncPollingTimeoutRef.current) {
      clearTimeout(powerPointSyncPollingTimeoutRef.current);
      powerPointSyncPollingTimeoutRef.current = null;
    }
    setIsSyncingPowerPoint(false);
  };

  const startPowerPointSyncPolling = (
    quoteId: number,
    previousFile: string | null | undefined,
    previousWebUrl: string | null | undefined
  ) => {
    powerPointSyncPollingRef.current = setInterval(async () => {
      try {
        const updatedQuote = await quotesService.getQuoteDetail(quoteId);
        const didChange =
          updatedQuote.power_point_presentation_file !== previousFile ||
          updatedQuote.power_point_presentation_web_url !== previousWebUrl;

        if (didChange) {
          stopPowerPointSyncPolling();
          await fetchQuoteDetail(quoteId, true);
          setSyncFeedback({
            type: 'success',
            message: 'PowerPoint presentation synced and refreshed.',
          });
        }
      } catch {
        // Continue polling on transient API failures.
      }
    }, 5000);

    powerPointSyncPollingTimeoutRef.current = setTimeout(async () => {
      stopPowerPointSyncPolling();
      await fetchQuoteDetail(quoteId, true);
      setSyncFeedback({
        type: 'success',
        message: 'Sync is still processing in the background. Refresh again shortly.',
      });
    }, 2 * 60 * 1000);
  };

  const handleSyncPowerPointFromOneDrive = async () => {
    if (!quote?.id) return;

    try {
      setIsSyncingPowerPoint(true);
      const previousPresentationFile = quote.power_point_presentation_file;
      const previousWebUrl = quote.power_point_presentation_web_url;
      const response = await quotesService.syncPowerPointPresentationFromOneDrive(quote.id);
      setSyncFeedback({
        type: 'success',
        message: `PowerPoint presentation sync started. Task ID: ${response.task_id}`,
      });
      startPowerPointSyncPolling(quote.id, previousPresentationFile, previousWebUrl);
    } catch (error: unknown) {
      stopPowerPointSyncPolling();
      setSyncFeedback({
        type: 'error',
        message: parseErrorMessage(
          error,
          'Unable to sync from OneDrive right now. Please try again later.'
        ),
      });
    }
  };

  const startAnalysisPolling = (quoteId: number, fromTrigger = false) => {
    if (analysisPollingRef.current || analysisPollingTimeoutRef.current) {
      stopAnalysisPolling();
    }
    analysisSurfacedRef.current = false;

    analysisPollingRef.current = setInterval(async () => {
      try {
        const updatedQuote = await quotesService.getQuoteDetail(quoteId);
        const isAnalysisReady = Boolean(updatedQuote.is_analysis_file_generated);
        const isPresentationReady = Boolean(
          updatedQuote.is_power_point_presentation_generated &&
            updatedQuote.power_point_presentation_file
        );

        if (isAnalysisReady && isPresentationReady) {
          stopAnalysisPolling();
          setIsProcessingNewVersion(false);
          fetchQuoteDetail(quoteId, true);
          setSuccessModal({
            isOpen: true,
            message: 'Analysis and presentation files generated successfully!',
          });
          return;
        }

        // The analysis file finishes before the (separately-generated)
        // PowerPoint. Surface it as soon as it's ready instead of blocking the
        // whole UI on the presentation, then keep polling for the presentation.
        if (isAnalysisReady && !analysisSurfacedRef.current) {
          analysisSurfacedRef.current = true;
          setIsProcessingNewVersion(false);
          fetchQuoteDetail(quoteId, true);
          if (fromTrigger) {
            setStatusNotice(
              'Analysis file is ready. The presentation is still generating and will appear here automatically.'
            );
          }
        }
      } catch {
        // Continue polling on error
      }
    }, ANALYSIS_POLL_INTERVAL_MS);

    analysisPollingTimeoutRef.current = setTimeout(async () => {
      stopAnalysisPolling();
      setIsProcessingNewVersion(false);
      // Self-heal: the job may have finished moments before the deadline, so
      // re-check before telling the user anything, and always refresh the view.
      try {
        const latest = await quotesService.getQuoteDetail(quoteId);
        fetchQuoteDetail(quoteId, true);
        const ready =
          Boolean(latest.is_analysis_file_generated) &&
          Boolean(
            latest.is_power_point_presentation_generated &&
              latest.power_point_presentation_file
          );
        if (ready) {
          setSuccessModal({
            isOpen: true,
            message: 'Analysis and presentation files generated successfully!',
          });
          return;
        }
      } catch {
        // fall through to the informational notice below
      }
      setSuccessModal({
        isOpen: true,
        message:
          'Your files are still generating in the background. They’ll appear here automatically — you can keep working or check back shortly.',
      });
    }, ANALYSIS_GENERATION_DEADLINE_MS);
  };

  const stopAnalysisPolling = () => {
    if (analysisPollingRef.current) {
      clearInterval(analysisPollingRef.current);
      analysisPollingRef.current = null;
    }
    if (analysisPollingTimeoutRef.current) {
      clearTimeout(analysisPollingTimeoutRef.current);
      analysisPollingTimeoutRef.current = null;
    }
  };

  // Resume polling after a reload: if the analysis file is ready but the
  // PowerPoint is still being generated, pick the job back up so a page refresh
  // doesn't strand an in-flight generation.
  useEffect(() => {
    if (!quote) return;
    const analysisReady = Boolean(quote.is_analysis_file_generated);
    const presentationReady = Boolean(
      quote.is_power_point_presentation_generated &&
        quote.power_point_presentation_file
    );
    if (analysisReady && !presentationReady && !analysisPollingRef.current) {
      startAnalysisPolling(quote.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quote?.is_analysis_file_generated,
    quote?.is_power_point_presentation_generated,
    quote?.power_point_presentation_file,
  ]);

  if (!loading && (error || !quote)) {
    return (
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}>
        <p style={{ color: 'var(--color-error-500)' }}>Error: {error || 'Quote not found'}</p>
        <button
          onClick={() => navigate('/quotes')}
          className="mt-4 px-4 py-2 rounded-lg text-white"
          style={{ backgroundColor: 'var(--color-primary-500)' }}
        >
          Back to Quotes
        </button>
      </div>
    );
  }

  if (loading || !quote) {
    return (
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}>
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent mb-4"></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading quote...</p>
        </div>
      </div>
    );
  }

  
  const formatDecimalToPercentage = (value: string | null | undefined, manualPremium?: string): string => {
    if (!value) return '0%';
    const str = String(value).trim();
    
    if (str.includes('%')) {
      const cleaned = str.replace(/%/g, '').trim();
      const num = parseFloat(cleaned);
      if (isNaN(num)) return '0%';
      if (num === 0) return '0%';
      const formatted = num % 1 === 0 ? `${num}%` : `${num.toFixed(2)}%`;
      return formatted;
    }
    
    const num = parseFloat(str);
    if (isNaN(num)) return '0%';
    
    if (manualPremium) {
      const premium = parseFloat(manualPremium.replace(/[$,]/g, '')) || 0;
      if (premium === 0) return '0%';
      const percentage = (num / premium) * 100;
      if (percentage === 0) return '0%';
      return percentage % 1 === 0 ? `${percentage}%` : `${percentage.toFixed(2)}%`;
    }
    
    if (num === 0) return '0%';
    const formatted = num % 1 === 0 ? `${num}%` : `${num.toFixed(2)}%`;
    return formatted;
  };

  const formatPercentage = (value: string | null | undefined): string => {
    if (!value) return '0.00%';
    const str = String(value).trim();

    if (str.includes('%')) {
      const cleaned = str.replace(/%/g, '').trim();
      const num = parseFloat(cleaned);
      if (isNaN(num)) return '0.00%';
      return `${num.toFixed(2)}%`;
    }

    const num = parseFloat(str);
    if (isNaN(num)) return '0.00%';
    return `${num.toFixed(2)}%`;
  };

  // Shared fallback used by both the Manual Parento Commission % row AND the
  // Quoted Parento Commission % row (when the override is 0/null). Keeping
  // them in one place guarantees they cannot drift.
  const formatManualParentoCommissionFallback = (
    manual: string | number | null | undefined,
  ): string => {
    if (!manual || parseFloat(String(manual)) === 0) return '15.00%';
    return formatPercentage(String(manual));
  };

  const displayOriginal = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '0';
    return String(value);
  };

  const calcPepm = (amount: string | null | undefined, coveredLives: string | null | undefined): number => {
    const a = parseFloat(String(amount ?? '0'));
    const cl = parseFloat(String(coveredLives ?? '0'));
    if (!cl || !isFinite(cl)) return 0;
    const result = a / cl / 12;
    return isFinite(result) ? result : 0;
  };

  const handleOutputFieldChange = (outputId: number, field: string, value: string) => {
    setEditableOutputs((prev) => ({
      ...prev,
      [outputId]: {
        ...prev[outputId],
        [field]: value,
      },
    }));
    // Clear any prior validation error while the user is typing — they get
    // another chance to land on a valid value before blur re-validates.
    // Uses a functional updater so it always reads the latest state, even
    // when this fires in the same event tick as a pending setError call.
    setOutputFieldErrors((prev) => {
      if (!prev[outputId]?.[field]) return prev;
      const nextRow = { ...prev[outputId] };
      delete nextRow[field];
      return { ...prev, [outputId]: nextRow };
    });
  };

  const handleOutputFieldBlur = async (outputId: number, field: string, value: string) => {
    if (!quote) return;

    const output = quote.outputs.find((o) => o.id === outputId);
    if (!output) return;

    const rawPreviousValue = output[field as keyof typeof output] as string || '0.00';
    const manualPremium = undefined; // All fields are now stored as percentages, not currency amounts
    const previousValue = formatDecimalToPercentage(rawPreviousValue, manualPremium);
    let finalValue = value.trim() === '' ? previousValue : value;

    if (finalValue && !finalValue.includes('%') && !isNaN(parseFloat(finalValue))) {
      finalValue = `${finalValue}%`;
    }

    // v3b: enforce PDF rule — UD and BD each must be > -100% AND their sum
    // must be > -100%. A sum violation flags BOTH inputs. SaaS BD must be
    // non-negative (separate rule, no pair semantics).
    if (
      field === 'underwriting_discretion' ||
      field === 'business_discretion'
    ) {
      const otherField =
        field === 'underwriting_discretion'
          ? 'business_discretion'
          : 'underwriting_discretion';
      const otherValue =
        editableOutputs[outputId]?.[otherField] ??
        formatDecimalToPercentage(output[otherField] as string);
      const uw = field === 'underwriting_discretion' ? finalValue : otherValue;
      const bus = field === 'business_discretion' ? finalValue : otherValue;

      const pairErrors = getDiscretionPairErrors(uw, bus);
      const hasErrors =
        !!pairErrors.underwriting_discretion || !!pairErrors.business_discretion;

      if (hasErrors) {
        // Keep the user's invalid value visible; flag UD and/or BD with red
        // border via outputFieldErrors. Block the PATCH.
        setEditableOutputs((prev) => ({
          ...prev,
          [outputId]: { ...prev[outputId], [field]: finalValue },
        }));
        setOutputFieldErrors((prev) => {
          const nextRow = { ...prev[outputId] };
          if (pairErrors.underwriting_discretion) {
            nextRow.underwriting_discretion = pairErrors.underwriting_discretion;
          } else {
            delete nextRow.underwriting_discretion;
          }
          if (pairErrors.business_discretion) {
            nextRow.business_discretion = pairErrors.business_discretion;
          } else {
            delete nextRow.business_discretion;
          }
          return { ...prev, [outputId]: nextRow };
        });
        return;
      }
      // Pair is valid — clear any lingering errors on either input.
      // Functional updater so we always operate on the latest state.
      setOutputFieldErrors((prev) => {
        if (
          !prev[outputId]?.underwriting_discretion &&
          !prev[outputId]?.business_discretion
        ) {
          return prev;
        }
        const nextRow = { ...prev[outputId] };
        delete nextRow.underwriting_discretion;
        delete nextRow.business_discretion;
        return { ...prev, [outputId]: nextRow };
      });
    } else if (
      field === 'business_discretion_percentage_for_sas' ||
      field === 'business_discretion_percentage_for_lms'
    ) {
      const validationError = validateSingleDiscretion(
        finalValue,
        field === 'business_discretion_percentage_for_lms' ? 'LMS' : 'SaaS',
      );
      if (validationError) {
        setEditableOutputs((prev) => ({
          ...prev,
          [outputId]: { ...prev[outputId], [field]: finalValue },
        }));
        setOutputFieldErrors((prev) => ({
          ...prev,
          [outputId]: { ...prev[outputId], [field]: validationError },
        }));
        return;
      }
      setOutputFieldErrors((prev) => {
        if (!prev[outputId]?.[field]) return prev;
        const nextRow = { ...prev[outputId] };
        delete nextRow[field];
        return { ...prev, [outputId]: nextRow };
      });
    }

    setEditableOutputs((prev) => ({
      ...prev,
      [outputId]: {
        ...prev[outputId],
        [field]: finalValue,
      },
    }));

    const formattedPrevious = formatDecimalToPercentage(rawPreviousValue, manualPremium);
    if (finalValue !== formattedPrevious) {
      try {
        const updatedOutput = await updateOutputField(quote.id, outputId, field, finalValue);
        
        if (updatedOutput) {
          setEditableOutputs((prev) => ({
            ...prev,
            [outputId]: {
              ...prev[outputId],
              [field]: formatDecimalToPercentage(updatedOutput[field as keyof typeof updatedOutput] as string),
            },
          }));
        }
      } catch (error) {
        // PATCH failed for a non-validation reason (network, stale celery,
        // unrelated BE error). Do NOT paint a red border on a locally-valid
        // input — that misleads the user. Revert the visible value to the
        // last-saved value and log for diagnostics.
        console.warn('Failed to update output field', error);
        setEditableOutputs((prev) => ({
          ...prev,
          [outputId]: { ...prev[outputId], [field]: formattedPrevious },
        }));
      }
    } else {
      setEditableOutputs((prev) => {
        const current = prev[outputId];
        if (!current || !current[field]) return prev;
        return {
          ...prev,
          [outputId]: {
            ...prev[outputId],
            [field]: formattedPrevious,
          },
        };
      });
    }
  };

  const handleOutputPlanFlagChange = async (
    outputId: number,
    patch: Partial<Pick<QuoteOutput, 'is_primary_plan' | 'is_sold_plan'>>
  ) => {
    if (!quote) return;

    const output = quote.outputs.find((o) => o.id === outputId);
    if (!output) return;

    const body: Partial<Pick<QuoteOutput, 'is_primary_plan' | 'is_sold_plan'>> = {};
    if (patch.is_primary_plan !== undefined) {
      const prev = output.is_primary_plan ?? false;
      if (patch.is_primary_plan !== prev) body.is_primary_plan = patch.is_primary_plan;
    }
    if (patch.is_sold_plan !== undefined) {
      const prev = output.is_sold_plan ?? false;
      if (patch.is_sold_plan !== prev) body.is_sold_plan = patch.is_sold_plan;
    }
    if (Object.keys(body).length === 0) return;

    try {
      await patchQuoteOutput(quote.id, outputId, body);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to update plan flags');
    }
  };

  const handleMarkAsSold = async () => {
    if (!soldPlanConfirm || !quote) return;
    setIsMarkingSold(true);
    try {
      await handleOutputPlanFlagChange(soldPlanConfirm.outputId, { is_sold_plan: true });
    } finally {
      setIsMarkingSold(false);
      setSoldPlanConfirm(null);
    }
  };

  const isTopPresentationDownloadReady =
    Boolean(quote.is_analysis_file_generated) &&
    Boolean(quote.analysis_file) &&
    Boolean(quote.is_power_point_presentation_generated) &&
    Boolean(quote.power_point_presentation_file);

  const renderDownloadIconWithBadge = (badge: 'C' | 'A' | 'P', size = 18) => (
    <span className="relative inline-flex items-center justify-center">
      <FaDownload size={size} />
      <span
        className="absolute -top-1 -right-1 rounded-full text-[9px] font-bold leading-none px-1 py-0.5"
        style={{
          backgroundColor: 'var(--color-primary-500)',
          color: 'white',
        }}
      >
        {badge}
      </span>
    </span>
  );

  return (
    <div className="space-y-6 w-full max-w-none">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/quotes')}
            className="mb-2 text-sm font-medium"
            style={{ color: 'var(--color-primary-500)' }}
          >
            ← Back to Quotes
          </button>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {quote.prospect_name} - {quote.quote_name} (v{quote.version})
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (quote) {
                  navigate('/quotes/new', { state: { prefill: quote } });
                }
              }}
              className="p-2 rounded hover:bg-[var(--color-hover-background)]"
              title="Clone"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <FaCopy size={18} />
            </button>
            <button
              onClick={() => {
                const variations = (quote.outputs ?? []).map((output, index) => {
                  const benefitPercent = Number(output.benefit_percent) || 0;
                  const weeklyCap = Number(output.weekly_cap) || 0;
                  const weeks = Number(output.length_of_leave_weeks) || 0;
                  const annualPremium =
                    Number(output.quoted_premium_with_taxes) ||
                    Number(output.total_program_costs_with_tax_stamping_fee) ||
                    Number(output.manual_premium) ||
                    0;
                  return {
                    id: String(output.id ?? index),
                    label: `${benefitPercent}% · $${weeklyCap.toLocaleString()} cap · ${weeks} wks`,
                    isSoldPlan: output.is_sold_plan === true,
                    benefitPercent,
                    weeklyCap,
                    lengthOfLeaveWeeks: weeks,
                    waitingPeriodDays: 7,
                    aggregateLimit: Math.round(weeklyCap * weeks * 40),
                    concurrency: output.concurrency_with_benefits || 'Concurrent',
                    sdiOffset: quote.sdi_offset === 'yes',
                    pflOffset: quote.pfl_offset === 'yes',
                    annualPremium,
                    costs: costsFromQuoteOutput(output),
                  };
                });
                navigate('/policy-admin/policies', {
                  state: {
                    prefill: {
                      quoteId: String(quote.id),
                      quoteName: quote.quote_name,
                      prospectName: quote.prospect_name,
                      contactName: quote.prospect_name,
                      product: 'Paid Family & Medical Leave',
                      effectiveDate: quote.policy_effective_date,
                      variations,
                    },
                  },
                });
              }}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white"
              title="Create a policy from this quote"
              style={{ backgroundColor: 'var(--color-primary-500)' }}
            >
              <FaFileContract size={14} /> Create Policy
            </button>
            <button
              onClick={downloadCensusFile}
              className="p-2 rounded hover:bg-[var(--color-hover-background)]"
              title="Download Census"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {renderDownloadIconWithBadge('C')}
            </button>
            <button
              onClick={quote.is_analysis_file_generated && quote.analysis_file ? downloadAnalysisFile : undefined}
              className="p-2 rounded hover:bg-[var(--color-hover-background)]"
              title={quote.is_analysis_file_generated ? 'Download Analysis File' : 'Analysis file not yet generated'}
              style={{
                color: 'var(--color-text-secondary)',
                opacity: quote.is_analysis_file_generated && quote.analysis_file ? 1 : 0.4,
                cursor: quote.is_analysis_file_generated && quote.analysis_file ? 'pointer' : 'default',
              }}
              disabled={!quote.is_analysis_file_generated || !quote.analysis_file}
            >
              {renderDownloadIconWithBadge('A')}
            </button>
            <button
              onClick={isTopPresentationDownloadReady ? downloadPowerPointPresentation : undefined}
              className="p-2 rounded hover:bg-[var(--color-hover-background)]"
              title={
                isTopPresentationDownloadReady
                  ? 'Download Presentation'
                  : 'Presentation not yet generated'
              }
              style={{
                color: 'var(--color-text-secondary)',
                opacity: isTopPresentationDownloadReady ? 1 : 0.4,
                cursor: isTopPresentationDownloadReady ? 'pointer' : 'default',
              }}
              disabled={!isTopPresentationDownloadReady}
            >
              {renderDownloadIconWithBadge('P')}
            </button>
            <button
              onClick={handleDeleteClick}
              className="p-2 rounded hover:bg-[var(--color-hover-background)]"
              title="Delete"
              style={{ color: 'var(--color-error-500)' }}
            >
              <FaTrash size={18} />
            </button>
          </div>
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              {isEditingStatus ? (
                <div className="w-64">
                  <Select
                    ref={statusSelectRef}
                    options={QUOTE_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                    value={statusValue}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    onBlur={handleStatusBlur}
                  />
                </div>
              ) : (
                <>
                  <Badge variant={getStatusVariant(quote.status)} size="md">
                    {QUOTE_STATUS_OPTIONS.find((opt) => opt.value === quote.status)?.label || quote.status}
                  </Badge>
                  <button
                    onClick={handleStatusEdit}
                    className="p-1 rounded hover:bg-[var(--color-hover-background)]"
                    title="Edit status"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <FaEdit size={14} />
                  </button>
                </>
              )}
            </div>
            {quote.status === 'closed_won' && (
              <button
                onClick={() => navigate(`/policies/new?quoteId=${quote.id}`)}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-opacity hover:opacity-90"
                title="Generate Policy"
                style={{ backgroundColor: 'var(--color-primary-500)', color: 'white' }}
              >
                <FaFileContract size={13} />
                <span>Generate Policy</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {syncFeedback && (
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            backgroundColor:
              syncFeedback.type === 'success'
                ? 'var(--color-success-50)'
                : 'var(--color-error-50)',
            borderColor:
              syncFeedback.type === 'success'
                ? 'var(--color-success-500)'
                : 'var(--color-error-500)',
            color:
              syncFeedback.type === 'success'
                ? 'var(--color-success-700)'
                : 'var(--color-error-700)',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{syncFeedback.message}</span>
            <button
              onClick={() => setSyncFeedback(null)}
              className="text-xs font-semibold px-2 py-1 rounded hover:opacity-80"
              style={{
                border: '1px solid currentColor',
              }}
              aria-label="Close sync feedback"
              title="Close"
            >
              X
            </button>
          </div>
        </div>
      )}

      {statusNotice && (
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            backgroundColor: 'var(--color-info-50, #eff6ff)',
            borderColor: 'var(--color-info-500, #3b82f6)',
            color: 'var(--color-info-700, #1d4ed8)',
          }}
        >
          {statusNotice}
        </div>
      )}

      {/* v3b: deprecated rater/analysis template banner */}
      {quote && !quote.can_regenerate_analysis && (
        <div
          className="rounded-lg border px-4 py-3 text-sm flex items-center justify-between gap-4"
          style={{
            backgroundColor: 'var(--color-warning-50, #fffbeb)',
            borderColor: 'var(--color-warning-500, #f59e0b)',
            color: 'var(--color-warning-700, #b45309)',
          }}
        >
          <div className="flex-1">
            <strong className="font-semibold">This quote uses an outdated rater or analysis template.</strong>
            <span className="ml-1">
              Re-running the analysis or editing rater-driven fields (override partner / commission) is disabled. Clone this quote to a new version to keep working.
            </span>
          </div>
          <button
            onClick={() => navigate('/quotes/new', { state: { prefill: quote } })}
            className="shrink-0 px-3 py-1.5 rounded text-xs font-semibold text-white"
            style={{ backgroundColor: 'var(--color-primary-500)' }}
            title="Clone this quote to a new version that uses the current rater + analysis template"
          >
            Clone to new version
          </button>
        </div>
      )}

      {quote && (
        <ConfirmDialog
          isOpen={deleteConfirm}
          title="Delete Quote"
          message={`Are you sure you want to delete "${quote.quote_name}" (v${quote.version})? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      <div
        className="rounded-lg border"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <button
          onClick={() => toggleSection('basic')}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Basic Information
          </h2>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {expandedSections.basic ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.basic && (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Prospect Name
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {quote.prospect_name}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Hubspot Company ID
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {quote.hubspot_company_id ?? '—'}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Quote Name
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {quote.quote_name}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Quote Type
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {QUOTE_TYPE_OPTIONS.find((opt) => opt.value === quote.quote_type)?.label || quote.quote_type}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Distribution Channel
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {DISTRIBUTION_CHANNEL_OPTIONS.find((opt) => opt.value === quote.distribution_channel)?.label || quote.distribution_channel || '—'}
              </p>
            </div>
            {quote.peo_group && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  PEO Group
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.peo_group.name}
                </p>
              </div>
            )}
            {quote.benefit_waiting_period_days != null && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Benefit Waiting Period (days)
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.benefit_waiting_period_days}
                </p>
              </div>
            )}
            {quote.sic_code && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  SIC Code
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.sic_code}
                </p>
              </div>
            )}
            {quote.for_profit_business && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  For-Profit Business
                </div>
                <p className="mt-1 capitalize" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.for_profit_business === 'yes' ? 'Yes' : 'No'}
                </p>
              </div>
            )}
            {quote.benefit_waiting_period_days !== null && quote.benefit_waiting_period_days !== undefined && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Benefit Waiting Period (days)
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.benefit_waiting_period_days}
                </p>
              </div>
            )}
            {quote.rater_file && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Rater File
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.rater_file.display_name || quote.rater_file.custom_file_name || quote.rater_file.file}
                  {quote.rater_file.description && (
                    <span className="text-sm ml-2" style={{ color: 'var(--color-text-secondary)' }}>
                      - {quote.rater_file.description}
                    </span>
                  )}
                </p>
              </div>
            )}
            {quote.analysis_template && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Analysis Template
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.analysis_template.display_name || quote.analysis_template.custom_file_name || quote.analysis_template.file}
                  {quote.analysis_template.description && (
                    <span className="text-sm ml-2" style={{ color: 'var(--color-text-secondary)' }}>
                      - {quote.analysis_template.description}
                    </span>
                  )}
                </p>
              </div>
            )}
            {quote.power_point_template && (
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  PowerPoint Template
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.power_point_template.display_name ||
                    quote.power_point_template.custom_file_name ||
                    quote.power_point_template.file}
                  {quote.power_point_template.description && (
                    <span className="text-sm ml-2" style={{ color: 'var(--color-text-secondary)' }}>
                      - {quote.power_point_template.description}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="rounded-lg border"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <button
          onClick={() => toggleSection('policy')}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Policy Details
          </h2>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {expandedSections.policy ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.policy && (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Policy State
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {POLICY_STATE_OPTIONS.find((opt) => opt.value === quote.policy_state)?.label || quote.policy_state}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Policy Effective Date
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {formatDate(quote.policy_effective_date)}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Generated Presentation
              </div>
              <div className="mt-1 flex items-center gap-2">
                {quote.is_power_point_presentation_generated && quote.power_point_presentation_file ? (
                  <>
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {quote.power_point_presentation_file_name ||
                        getFileNameFromUrl(quote.power_point_presentation_file)}
                    </span>
                    <button
                      onClick={downloadPowerPointPresentation}
                      className="p-1 rounded hover:bg-[var(--color-hover-background)]"
                      title="Download Presentation"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <FaDownload size={14} />
                    </button>
                    <button
                      onClick={handleSyncPowerPointFromOneDrive}
                      className="p-1 rounded hover:bg-[var(--color-hover-background)] disabled:opacity-50"
                      title="Sync latest from OneDrive"
                      style={{ color: 'var(--color-text-secondary)' }}
                      disabled={isSyncingPowerPoint}
                    >
                      <FaSyncAlt size={14} className={cn(isSyncingPowerPoint && 'animate-spin')} />
                    </button>
                  </>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Not available yet
                  </p>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Open in OneDrive
              </div>
              <div className="mt-1">
                {quote.power_point_presentation_web_url ? (
                  <a
                    href={quote.power_point_presentation_web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm underline"
                    style={{ color: 'var(--color-primary-500)' }}
                  >
                    Open in OneDrive
                  </a>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Not available yet
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="rounded-lg border"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <button
          onClick={() => toggleSection('census')}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Census & Employee Information
          </h2>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {expandedSections.census ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.census && (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Census File
              </div>
              <div className="mt-1">
                {quote.census_file ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {quote.census_file_name || 'Census File'}
                    </span>
                    <button
                      onClick={downloadCensusFile}
                      className="p-1 rounded hover:bg-[var(--color-hover-background)]"
                      title="Download Census File"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <FaDownload size={14} />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    No file uploaded
                  </p>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Employees Accepted
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {quote.employee_count}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Employees Ignored
                <Tooltip
                  content={
                    <div className="space-y-1">
                      {Object.entries(censusIgnoredReasonLabels).map(([key, label]) => {
                        const count = quote.census_ignored_reasons?.[
                          key as keyof typeof censusIgnoredReasonLabels
                        ] || 0;
                        return (
                          <div key={key}>
                            {label}: {count}
                          </div>
                        );
                      })}
                    </div>
                  }
                >
                  <span
                    className="ml-2 inline-flex items-center justify-center cursor-help rounded-full text-[10px] font-bold leading-none w-4 h-4"
                    aria-label="Employees ignored reasons"
                    style={{
                      backgroundColor: 'var(--color-primary-500)',
                      color: 'white',
                    }}
                  >
                    i
                  </span>
                </Tooltip>
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {quote.employees_ignored_count || 0}
              </p>
            </div>
          </div>
        )}
      </div>

      {quote.insurance_plan && (
        <div
          className="rounded-lg border"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <button
            onClick={() => toggleSection('insurance')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Insurance Plan
            </h2>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {expandedSections.insurance ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.insurance && (
            <div className="px-6 pb-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Row 1: Benefits | Weekly Cap 1 | Length of Leave Weeks */}
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Benefits
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.benefits?.join(', ') || '-'}
                  </p>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Weekly Cap 1
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {formatCurrency(quote.insurance_plan.weekly_cap_1)}
                  </p>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Length of Leave Weeks
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.length_of_leave_weeks?.join(', ') || '-'}
                  </p>
                </div>
                {/* Optional extras after main row 1 */}
                {quote.insurance_plan.weekly_cap_2 && (
                  <div>
                    <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Weekly Cap 2
                    </div>
                    <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                      {formatCurrency(quote.insurance_plan.weekly_cap_2)}
                    </p>
                  </div>
                )}
                {quote.insurance_plan.weekly_cap_3 && (
                  <div>
                    <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Weekly Cap 3
                    </div>
                    <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                      {formatCurrency(quote.insurance_plan.weekly_cap_3)}
                    </p>
                  </div>
                )}
                {quote.insurance_plan.length_of_leave_weeks_sec && (
                  <div>
                    <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Length of Leave Weeks Secondary
                    </div>
                    <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                      {quote.insurance_plan.length_of_leave_weeks_sec.join(', ')}
                    </p>
                  </div>
                )}
                {/* Row 2: Length of Service | Concurrency with Benefits | Aggregate Benefit */}
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Length of Service
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.length_of_service?.join(', ') || '-'}
                  </p>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Concurrency with Benefits
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.concurrency_with_benefits?.toLowerCase().trim() === 'concurrently' ? 'Concurrently' : 'Non-Concurrently'}
                  </p>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Aggregate Benefit
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.aggregate_benefit === 'unlimited'
                      ? 'Unlimited'
                      : `$${parseInt(quote.insurance_plan.aggregate_benefit).toLocaleString()}`}
                  </p>
                </div>
                {/* Row 3: Private STD Plan | STD Benefit Percentage | Maximum Weekly Payment */}
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Private STD Plan
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.private_std_plan === 'yes' ? 'Yes' : 'No'}
                  </p>
                </div>
                {quote.insurance_plan.private_std_plan === 'yes' && (
                  <>
                    {quote.insurance_plan.pay_for_std_plan && (
                      <div>
                        <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                          STD Benefit Percentage
                        </div>
                        <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                          {quote.insurance_plan.pay_for_std_plan}%
                        </p>
                      </div>
                    )}
                    {quote.insurance_plan.maximum_weekly_payment && (
                      <div>
                        <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                          Maximum Weekly Payment
                        </div>
                        <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                          {formatCurrency(quote.insurance_plan.maximum_weekly_payment)}
                        </p>
                      </div>
                    )}
                    {/* Row 4 start: Waiting Period Days */}
                    {quote.insurance_plan.waiting_period_days && (
                      <div>
                        <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                          Waiting Period Days
                        </div>
                        <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                          {quote.insurance_plan.waiting_period_days}
                        </p>
                      </div>
                    )}
                  </>
                )}
                {/* Row 4 (or continues): SDI Offset | PFL Offset */}
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    SDI Offset
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.sdi_offset === 'yes' ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    PFL Offset
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.insurance_plan.pfl_offset === 'yes' ? 'Yes' : 'No'}
                  </p>
                </div>
                {/* PRTO-8: Include LMS — Yes | No | Both */}
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Include LMS
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {INCLUDE_LMS_OPTIONS.find((option) => option.value === quote.insurance_plan?.include_lms)?.label ?? '-'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {quote.handbook_plan && (
        <div
          className="rounded-lg border"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <button
            onClick={() => toggleSection('handbook')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Handbook Plan
            </h2>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {expandedSections.handbook ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.handbook && (
            <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Plan Type
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.handbook_plan.plan_type === 'standard' ? 'Standard' : 'Primary and Secondary'}
                </p>
              </div>
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Length of Leave Weeks
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.handbook_plan.length_of_leave_weeks}
                </p>
              </div>
              {quote.handbook_plan.length_of_leave_weeks_sec && (
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Length of Leave Weeks Secondary
                  </div>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {quote.handbook_plan.length_of_leave_weeks_sec}
                  </p>
                </div>
              )}
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Benefit
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {quote.handbook_plan.benefit}%
                </p>
              </div>
              <div>
                <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Weekly Cap
                </div>
                <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                  {formatCurrency(quote.handbook_plan.weekly_cap)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="rounded-lg border overflow-hidden"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <button
          onClick={() => toggleSection('outputs')}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Quote Outputs
          </h2>
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={(e) => { e.stopPropagation(); setIsSimpleView((v) => !v); }}
              className="px-3 py-1 text-xs font-medium rounded-full border transition-colors"
              style={{
                backgroundColor: isSimpleView ? 'var(--color-primary-500)' : 'transparent',
                color: isSimpleView ? '#fff' : 'var(--color-text-secondary)',
                borderColor: 'var(--color-primary-500)',
              }}
            >
              {isSimpleView ? 'Simple View' : 'Full View'}
            </button>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {expandedSections.outputs ? '▼' : '▶'}
            </span>
          </div>
        </button>
        {expandedSections.outputs && (
          <div className="pb-6 space-y-4">
            {/* Item 38: generation failed terminally — surface the cause instead
                of an endless "generating…" spinner. */}
            {quote.generation_failed && (
              <div className="mt-4 mx-6 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-error-50, #fef2f2)', borderColor: 'var(--color-error-300, #fca5a5)' }}>
                <div className="text-sm font-semibold mb-1" style={{ color: 'var(--color-error-700, #b91c1c)' }}>
                  Output generation failed
                </div>
                <div className="text-sm" style={{ color: 'var(--color-error-700, #b91c1c)' }}>
                  {quote.generation_error || 'The rater could not generate outputs for this quote. Please retry, or re-upload the rater file if the problem persists.'}
                </div>
              </div>
            )}
            {showProgress && quote.expected_outputs_count !== undefined && quote.expected_outputs_count > 0 && (
              <div className="mt-4 mx-6 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border-light)' }}>
                <div className="mb-2">
                  <div className="w-full rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-neutral-200)', height: '8px' }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${quote.outputs_progress || 0}%`,
                        backgroundColor: 'var(--color-primary-500)',
                      }}
                    />
                  </div>
                </div>
                
                <div className="text-sm text-center mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {quote.outputs_count || 0} / {quote.expected_outputs_count} variations generated
                  {quote.outputs_progress !== undefined && ` (${quote.outputs_progress}%)`}
                </div>

                {isGenerating && (
                  <div className="flex items-center justify-center gap-2 mt-2" style={{ color: 'var(--color-primary-500)' }}>
                    <div
                      className="animate-spin rounded-full border-2"
                      style={{
                        width: '16px',
                        height: '16px',
                        borderColor: 'var(--color-primary-500)',
                        borderTopColor: 'transparent',
                      }}
                    />
                    <span className="text-sm">Generating variations...</span>
                  </div>
                )}

                {quote.all_outputs_generated && (
                  <div className="mt-2 p-2 rounded text-sm text-center" style={{ backgroundColor: 'var(--color-success-50)', color: 'var(--color-success-700)' }}>
                    ✓ All variations generated successfully
                  </div>
                )}
              </div>
            )}

            {quote.outputs && quote.outputs.length > 0 && (
              <p className="text-sm mb-2 ml-6" style={{ color: 'var(--color-text-secondary)' }}>
                You can select sold plan by clicking on one of the variants.
              </p>
            )}

            {quote.outputs && quote.outputs.length > 0 ? (
            <div className="relative w-full">
              <div
                ref={matrixTopScrollRef}
                onScroll={handleMatrixTopScroll}
                className="mb-1 overflow-x-auto overflow-y-hidden"
                aria-hidden
              >
                <div ref={matrixScrollSpacerRef} className="h-4 shrink-0" />
              </div>
              <div
                ref={matrixBottomScrollRef}
                onScroll={handleMatrixBottomScroll}
                className="overflow-x-auto relative"
              >
              <table
                className={cn(
                  'quote-detail-outputs-matrix min-w-full border-collapse',
                  quote.outputs.length > QUOTE_OUTPUTS_MATRIX_SCROLL_OVER_VARIATIONS
                    ? 'quote-detail-outputs-matrix--scroll lg:w-max lg:min-w-full'
                    : 'lg:table-fixed lg:w-full'
                )}
                style={{
                  border: '1px solid var(--color-border-light)',
                  borderSpacing: 0,
                  ['--quote-matrix-var-count' as string]: quote.outputs.length,
                }}
              >
                <colgroup>
                  <col />
                  <col />
                  {quote.outputs.map((output) => (
                    <col key={output.id} />
                  ))}
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-matrix-headerRow)', borderBottom: '2px solid var(--color-border-light)' }}>
                    <th className="sticky left-0 z-30 w-6 min-w-[1.5rem] border-r" style={{ backgroundColor: 'var(--color-matrix-headerRow)', borderColor: 'var(--color-border-light)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}></th>
                    <th className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-semibold text-left border-r" style={{ backgroundColor: 'var(--color-matrix-headerRow)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Field
                    </th>
                    {quote.outputs.map((output, index) => (
                      <th key={output.id} className="px-1 py-1.5 border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', backgroundColor: output.is_sold_plan ? 'var(--color-success-100, #dcfce7)' : 'var(--color-matrix-headerCell)' }}>
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-semibold">
                            <span
                              className="underline cursor-pointer hover:opacity-70"
                              title="Click to mark as sold plan"
                              role="button"
                              tabIndex={0}
                              onClick={() => setSoldPlanConfirm({ outputId: output.id, variationIndex: index + 1 })}
                              onKeyDown={(e) => e.key === 'Enter' && setSoldPlanConfirm({ outputId: output.id, variationIndex: index + 1 })}
                            >{index + 1}</span>
                            <input
                              type="checkbox"
                              title="Include in version / selection"
                              checked={selectedVariations.has(output.id)}
                              onChange={(e) => {
                                const nextChecked = e.target.checked;
                                // Local Set update first — keeps the checkbox
                                // responsive while the PATCH is in flight.
                                setSelectedVariations((prev) => {
                                  const next = new Set(prev);
                                  if (nextChecked) next.add(output.id);
                                  else next.delete(output.id);
                                  return next;
                                });
                                // Persist to BE so the selection survives
                                // reload / drives analysis generation server-side.
                                patchQuoteOutput(quote.id, output.id, {
                                  is_selected_for_analysis: nextChecked,
                                }).catch(() => {
                                  // Revert local Set on failure so the UI matches
                                  // server truth.
                                  setSelectedVariations((prev) => {
                                    const next = new Set(prev);
                                    if (nextChecked) next.delete(output.id);
                                    else next.add(output.id);
                                    return next;
                                  });
                                });
                              }}
                              className="h-4 w-4 shrink-0 rounded cursor-pointer"
                              style={{ accentColor: 'var(--color-primary-500)', borderColor: 'var(--color-border-light)' }}
                            />
                          </div>
                          {output.is_sold_plan && (
                            <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-success-500, #22c55e)', color: '#fff' }}>
                              Sold
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: 'var(--color-matrix-inputRow)', borderBottom: '1px solid var(--color-border-light)' }}>
                    <td className="sticky left-0 z-30 w-6 min-w-[1.5rem] border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}></td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-bold border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Primary / Sold
                    </td>
                    {quote.outputs.map((output, index) => (
                      <td key={index} className="px-1 py-1.5 text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-headerCell)' }}>
                        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
                          <label className="flex cursor-pointer items-center gap-0.5 text-[10px] leading-none sm:text-xs" style={{ color: 'var(--color-text-primary)' }}>
                            <input
                              type="checkbox"
                              title="Primary plan"
                              checked={output.is_primary_plan ?? false}
                              onChange={(e) =>
                                handleOutputPlanFlagChange(output.id, { is_primary_plan: e.target.checked })
                              }
                              className="h-3.5 w-3.5 shrink-0 rounded cursor-pointer"
                              style={{ accentColor: 'var(--color-primary-500)', borderColor: 'var(--color-border-light)' }}
                            />
                            <span className="whitespace-nowrap">Primary</span>
                          </label>
                        </div>
                      </td>
                    ))}
                  </tr>

                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                    <td rowSpan={6} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Inputs
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Length of Leave
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0.5 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                        {output.length_of_leave_weeks ? `${output.length_of_leave_weeks} weeks` : '-'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Length of Leave (Secondary)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-1 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                        {output.length_of_leave_weeks_sec && output.length_of_leave_weeks_sec !== 'N/A' ? `${output.length_of_leave_weeks_sec} weeks` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Benefit %
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-1 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                        {output.benefit_percent ? `${output.benefit_percent}%` : '-'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Weekly Cap
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-1 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                        {output.weekly_cap ? formatCurrency(output.weekly_cap) : '-'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Length of Service
                    </td>
                    {quote.outputs.map((output, idx) => {
                      let displayValue = '-';
                      if (output.length_of_service) {
                        if (Array.isArray(output.length_of_service)) {
                          displayValue = output.length_of_service.join(', ');
                        } else {
                          displayValue = output.length_of_service;
                        }
                      }
                      return (
                        <td key={idx} className="px-1 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Concurrency with other benefits
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-1 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                        {output.concurrency_with_benefits ? (output.concurrency_with_benefits.toLowerCase().trim() === 'concurrently' ? 'Concurrently' : 'Non-Concurrently') : '-'}
                      </td>
                    ))}
                  </tr>

                  {/* Simple View: standalone summary rows shown only when isSimpleView */}
                  {isSimpleView && (
                    <>
                      {/* Include LMS — mirrors the row in the Full View LMS
                          section, surfaced here because on a "Both" quote it is
                          the only thing telling the WITH-LMS and WITHOUT-LMS
                          columns apart. Input styling so it reads as a
                          continuation of the Inputs block directly above. */}
                      <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-inputRow)' }}>
                        <td className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)' }} />
                        <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-inputCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                          Include LMS
                        </td>
                        {quote.outputs.map((output, idx) => (
                          <td key={idx} className="px-1 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-primary-500)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-inputRow)' }}>
                            {output.include_lms === 'no' ? 'No' : 'Yes'}
                          </td>
                        ))}
                      </tr>
                      {/* Item 39: % Coverage of Employees < 40 y.o. — placed
                          directly below the Inputs block (Concurrency …). */}
                      <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                        <td className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)' }} />
                        <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                          % Coverage of Employees &lt; 40 y.o.
                        </td>
                        {quote.outputs.map((output, idx) => (
                          <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                            {formatPercentage(output.percentage_under_40)}
                          </td>
                        ))}
                      </tr>
                      <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                        <td className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)' }} />
                        <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                          Total Program Costs w/ Tax/Stamping/Issuance Fees
                        </td>
                        {quote.outputs.map((output, idx) => (
                          <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                            {formatCurrency(output.total_program_costs_with_tax_stamping_fee)}
                          </td>
                        ))}
                      </tr>
                      <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                        <td className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)' }} />
                        <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                          Total Program Costs w/ Tax/Stamping/Issuance (PEPM)
                        </td>
                        {quote.outputs.map((output, idx) => (
                          <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                            {formatCurrency(calcPepm(output.total_program_costs_with_tax_stamping_fee, output.covered_lives))}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}

                  {/* Full View: all detail sections (Insurance Premium → Rating Factors) */}
                  {!isSimpleView && (
                  <>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td rowSpan={14} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Insurance Premium
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Manual Premium
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatCurrency(output.manual_premium)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Underwriting Discretion
                    </td>
                    {quote.outputs.map((output, idx) => {
                      const fieldError = outputFieldErrors[output.id]?.underwriting_discretion;
                      const defaultBorder = fieldError
                        ? 'var(--color-error-500)'
                        : 'var(--color-border-light)';
                      return (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        <input
                          type="text"
                          value={editableOutputs[output.id]?.underwriting_discretion ?? formatDecimalToPercentage(output.underwriting_discretion)}
                          onChange={(e) => handleOutputFieldChange(output.id, 'underwriting_discretion', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = 'none';
                            e.target.style.borderColor = '';
                            handleOutputFieldBlur(output.id, 'underwriting_discretion', e.target.value);
                          }}
                          title={fieldError ?? ''}
                          className="w-20 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-1"
                          style={{
                            borderColor: defaultBorder,
                            backgroundColor: 'var(--color-background-paper)',
                            color: 'var(--color-text-primary)',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary-500)';
                            e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
                          }}
                        />
                      </td>
                      );
                    })}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Business Discretion
                    </td>
                    {quote.outputs.map((output, idx) => {
                      const fieldError = outputFieldErrors[output.id]?.business_discretion;
                      const defaultBorder = fieldError
                        ? 'var(--color-error-500)'
                        : 'var(--color-border-light)';
                      return (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        <input
                          type="text"
                          value={editableOutputs[output.id]?.business_discretion ?? formatDecimalToPercentage(output.business_discretion)}
                          onChange={(e) => handleOutputFieldChange(output.id, 'business_discretion', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = 'none';
                            e.target.style.borderColor = '';
                            handleOutputFieldBlur(output.id, 'business_discretion', e.target.value);
                          }}
                          className="w-20 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-1"
                          style={{
                            borderColor: defaultBorder,
                            backgroundColor: 'var(--color-background-paper)',
                            color: 'var(--color-text-primary)',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary-500)';
                            e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
                          }}
                        />
                      </td>
                      );
                    })}
                  </tr>
                  {/* v3b: Strategy Adjustment (premium) — OUT_PREM_STRAT_ADJ */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Strategy Adjustment
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatPercentage(output.strategy_adjustment_premium)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted Premium
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatCurrency(output.quoted_premium)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Surplus Lines Tax %
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatPercentage(output.surplus_lines_tax_percentage)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Stamping Fee %
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatPercentage(output.stamping_fee_tax_percentage)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Surplus Lines Tax
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatCurrency(output.surplus_lines_tax)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Stamping Fee
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatCurrency(output.stamping_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Policy Issuance Fee
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatCurrency(output.policy_issuance_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted Premium With Taxes
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatCurrency(output.quoted_premium_with_taxes)}
                      </td>
                    ))}
                  </tr>
                  {/* Manual Parento Commission % — rater default; shows 15% when null/zero */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Manual Parento Commission %
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {formatManualParentoCommissionFallback(output.manual_parento_commission_percentage)}
                      </td>
                    ))}
                  </tr>
                  {/* v3b col-J: Parento Commission % Override */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Parento Commission % Override
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        <input
                          type="text"
                          disabled={!quote.can_regenerate_analysis}
                          value={
                            // FE-only model: the override input is just another
                            // editable field. Show the in-flight edit if any,
                            // else the saved server value, else fall back to
                            // Manual Parento % so the user sees the effective
                            // commission rather than a bare blank.
                            editableOutputs[output.id]?.parento_commission_percentage_override ??
                            (output.parento_commission_percentage_override
                              ? formatDecimalToPercentage(output.parento_commission_percentage_override)
                              : formatManualParentoCommissionFallback(output.manual_parento_commission_percentage))
                          }
                          onChange={(e) =>
                            handleOutputFieldChange(
                              output.id,
                              'parento_commission_percentage_override',
                              e.target.value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary-500)';
                            e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = 'var(--color-border-light)';
                            e.target.style.boxShadow = 'none';
                            // Empty → clear override (use Manual). Anything
                            // else → updateOutputField PATCHes the override
                            // AND every FE-derived downstream value.
                            const raw = e.target.value.replace(/%/g, '').trim();
                            handleOutputFieldBlur(
                              output.id,
                              'parento_commission_percentage_override',
                              raw,
                            );
                          }}
                          className="w-20 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-1"
                          style={{
                            borderColor: 'var(--color-border-light)',
                            backgroundColor: 'var(--color-background-paper)',
                            color: 'var(--color-text-primary)',
                          }}
                          placeholder="0"
                          title={!quote.can_regenerate_analysis ? 'This quote uses an outdated rater or analysis template — clone to a new version to regenerate.' : ''}
                        />
                      </td>
                    ))}
                  </tr>
                  {/* v3b: Quoted Parento Commission % — IF override != 0 use override, ELSE use manual (default 15%).
                      Reads in-flight typing from editableOutputs first (instant feedback), then the saved
                      parento_commission_percentage which the FE writes on blur via updateOutputField. */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted Parento Commission %
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)', paddingLeft: '0', paddingRight: '0' }}>
                        {(() => {
                          // Prefer the in-flight typed override (instant feedback while user types).
                          const editing = editableOutputs[output.id]?.parento_commission_percentage_override;
                          if (editing !== undefined && editing !== '') {
                            const num = parseFloat(String(editing).replace(/%/g, ''));
                            if (!isNaN(num) && num !== 0) {
                              return formatDecimalToPercentage(String(editing));
                            }
                          }
                          // Server value (updateOutputField writes both the override and the
                          // resulting parento_commission_percentage on blur).
                          if (output.parento_commission_percentage_override) {
                            const num = parseFloat(String(output.parento_commission_percentage_override));
                            if (!isNaN(num) && num !== 0) {
                              return formatDecimalToPercentage(
                                String(output.parento_commission_percentage_override),
                              );
                            }
                          }
                          return formatManualParentoCommissionFallback(
                            output.manual_parento_commission_percentage,
                          );
                        })()}
                      </td>
                    ))}
                  </tr>

                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td rowSpan={4} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          SaaS
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Manual SaaS Fee
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatCurrency(output.manual_sas_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Business Discretion % for SaaS
                    </td>
                    {quote.outputs.map((output, idx) => {
                      const fieldError = outputFieldErrors[output.id]?.business_discretion_percentage_for_sas;
                      const defaultBorder = fieldError
                        ? 'var(--color-error-500)'
                        : 'var(--color-border-light)';
                      return (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        <input
                          type="text"
                          value={editableOutputs[output.id]?.business_discretion_percentage_for_sas ?? formatDecimalToPercentage(output.business_discretion_percentage_for_sas)}
                          onChange={(e) => handleOutputFieldChange(output.id, 'business_discretion_percentage_for_sas', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = 'none';
                            e.target.style.borderColor = '';
                            handleOutputFieldBlur(output.id, 'business_discretion_percentage_for_sas', e.target.value);
                          }}
                          title={fieldError ?? ''}
                          className="w-20 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-1"
                          style={{
                            borderColor: defaultBorder,
                            backgroundColor: 'var(--color-background-paper)',
                            color: 'var(--color-text-primary)',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary-500)';
                            e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
                          }}
                        />
                      </td>
                      );
                    })}
                  </tr>
                  {/* v3b: Strategy Adjustment (SaaS) — OUT_SAS_STRAT_ADJ */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Strategy Adjustment
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatPercentage(output.strategy_adjustment_sas)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted SaaS Fee
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatCurrency(output.quoted_sas_fee)}
                      </td>
                    ))}
                  </tr>

                  {/* Rating Engine 1.3.3: LMS fee block, mirroring SaaS above.
                      "Include LMS" says which variant each column was priced
                      as — a quote set to Both has one column per variant.
                      PRTO-9a: white background, unlike the SaaS yellow. */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td rowSpan={5} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          LMS
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Include LMS
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        {output.include_lms === 'no' ? 'No' : 'Yes'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Manual LMS Fee
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        {formatCurrency(output.manual_lms_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Business Discretion % for LMS
                    </td>
                    {quote.outputs.map((output, idx) => {
                      const fieldError = outputFieldErrors[output.id]?.business_discretion_percentage_for_lms;
                      const defaultBorder = fieldError
                        ? 'var(--color-error-500)'
                        : 'var(--color-border-light)';
                      return (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        <input
                          type="text"
                          value={editableOutputs[output.id]?.business_discretion_percentage_for_lms ?? formatDecimalToPercentage(output.business_discretion_percentage_for_lms)}
                          onChange={(e) => handleOutputFieldChange(output.id, 'business_discretion_percentage_for_lms', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = 'none';
                            e.target.style.borderColor = '';
                            handleOutputFieldBlur(output.id, 'business_discretion_percentage_for_lms', e.target.value);
                          }}
                          title={fieldError ?? ''}
                          className="w-20 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-1"
                          style={{
                            borderColor: defaultBorder,
                            backgroundColor: 'var(--color-background-paper)',
                            color: 'var(--color-text-primary)',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary-500)';
                            e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
                          }}
                        />
                      </td>
                      );
                    })}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Strategy Adjustment
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        {formatPercentage(output.strategy_adjustment_lms)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted LMS Fee
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        {formatCurrency(output.quoted_lms_fee)}
                      </td>
                    ))}
                  </tr>

                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                    <td rowSpan={3} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Total Costs
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Total Program Cost w/o Tax/Stamping/Issuance Fees
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                        {formatCurrency(output.total_program_cost_without_tax_and_stamping_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Tax/Stamping/Issuance Fees
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                        {formatCurrency(output.tax_and_stamping_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Total Program Costs w/ Tax/Stamping/Issuance Fees
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                        {formatCurrency(output.total_program_costs_with_tax_stamping_fee)}
                      </td>
                    ))}
                  </tr>

                  {/* v3b col-J: PEO section — white background per reviewer */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td rowSpan={2} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          PEO
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      PEO Group
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        {output.peo_group_output || '—'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      PEO Strategy Applied
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-background-paper)' }}>
                        {output.peo_strategy_applied || '—'}
                      </td>
                    ))}
                  </tr>

                  {/* v3b: Reinsurance Partner section */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                    <td rowSpan={3} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Reinsurance
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Manual Reinsurance Partner
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                        {output.manual_reinsurance_partner || '—'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Reinsurance Partner Override
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-center border-r" style={{ borderColor: 'var(--color-border-light)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                        <select
                          disabled={!quote.can_regenerate_analysis}
                          value={output.reinsurance_partner_override || ''}
                          onChange={(e) => {
                            // Route through updateOutputField so the partner
                            // change re-runs recalculateOutputFields and PATCHes
                            // the full derived bundle (Policy Issuance Fee +
                            // Tax/Stamping/Issuance + Quoted Premium With Taxes
                            // + TPC With + PEPM-w-tax + Net Cost). Using
                            // patchQuoteOutput directly only saves the override
                            // field and leaves every downstream cell stale.
                            updateOutputField(
                              quote.id,
                              output.id,
                              'reinsurance_partner_override',
                              e.target.value,
                            );
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary-500)';
                            e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = 'var(--color-border-light)';
                            e.target.style.boxShadow = 'none';
                          }}
                          className="w-32 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-1"
                          style={{
                            borderColor: 'var(--color-border-light)',
                            backgroundColor: 'var(--color-background-paper)',
                            color: 'var(--color-text-primary)',
                          }}
                          title={!quote.can_regenerate_analysis ? 'This quote uses an outdated rater or analysis template — clone to a new version to regenerate.' : ''}
                        >
                          {/* Empty value still means "no override applied" — the
                              rater treats it as: use the Manual partner. Label the
                              option with the Manual partner name + "(default)" so
                              the dropdown's visible text matches the effective
                              partner instead of showing a confusing blank. */}
                          <option value="">
                            {output.manual_reinsurance_partner
                              ? `${output.manual_reinsurance_partner} (default)`
                              : '(default)'}
                          </option>
                          {reinsurancePartners.map((p) => (
                            <option key={p.id} value={p.name}>{p.name}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted Reinsurance Partner
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowRow)' }}>
                        {output.reinsurance_partner_override?.trim()
                          ? output.reinsurance_partner_override.trim()
                          : (output.quoted_reinsurance_partner || output.manual_reinsurance_partner || '—')}
                      </td>
                    ))}
                  </tr>

                  {/* v3b col-J: Tax Credits — light grey per reviewer */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td rowSpan={6} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Tax Credits
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Federal Tax Credit %
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatPercentage(output.tax_credit_percent)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Applicable % of Premium Qualifying
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatPercentage(output.tax_credit_app_premium_percent)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Tax Credit Qualifying Premium
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatCurrency(output.tax_credit_app_premium)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Tax Credit
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatCurrency(output.tax_credit_amount)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Net Cost After Tax Credit
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatCurrency(output.net_cost_after_tax_credit)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-grayRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-grayCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Net Cost After Tax Credit (incl Tax/Stamping/Issuance)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-grayRow)' }}>
                        {formatCurrency(output.net_cost_after_tax_credit_with_tax_fee)}
                      </td>
                    ))}
                  </tr>

                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td rowSpan={7} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          PEPM
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted Premium PEPM
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(calcPepm(output.quoted_premium, output.covered_lives))}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted SaaS Fee PEPM
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(calcPepm(output.quoted_sas_fee, output.covered_lives))}
                      </td>
                    ))}
                  </tr>
                  {/* Rating Engine 1.3.3: OUT_PEPM_LMS */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted LMS Fee PEPM
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(calcPepm(output.quoted_lms_fee, output.covered_lives))}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Quoted Premium + SaaS + LMS Fee PEPM
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(calcPepm(output.quoted_premium, output.covered_lives) + calcPepm(output.quoted_sas_fee, output.covered_lives) + calcPepm(output.quoted_lms_fee, output.covered_lives))}
                      </td>
                    ))}
                  </tr>
                  {/* v3b: PEPM extras */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Total Program Costs w/ Tax/Stamping/Issuance (PEPM)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(calcPepm(output.total_program_costs_with_tax_stamping_fee, output.covered_lives))}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Parento Commissions PEPM
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(calcPepm(output.parento_commission, output.covered_lives))}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-orangeRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-orangeCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Total Parento Revenue (Comm + SaaS + LMS Fee)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-orangeRow)' }}>
                        {formatCurrency(output.parento_total_revenue)}
                      </td>
                    ))}
                  </tr>

                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td rowSpan={17} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Key Metrics
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      % Coverage of Employees &lt; 40 y.o.
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.percentage_under_40)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Average Age by Count
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {displayOriginal(output.average_age_by_count)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Median Age
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {displayOriginal(output.median_age)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Female % By Employee Count
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.female_percentage_by_employee_count)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Salary Replacement Ratio
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.salary_replacement_ratio)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Average Weekly Salary
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {displayOriginal(output.average_weekly_salary)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Average Tenure in Months
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {displayOriginal(output.average_tenure_in_months)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Length of Service Eligibility Impact
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.los_eligibility_impact)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Aggregate Handbook Benefit Replacement Ratio
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.aggregate_benefit_replacement_ratio)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      % of Total Exposure Covered By Insurance Policy
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.percentage_total_exposure_covered_by_ins_policy)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Average Claim Amount Paid By Insurance Policy
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatCurrency(output.avg_claim_amt_paid_by_ins_policy)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Risk Adjusted Average Claim Amount Paid By Insurance Policy
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatCurrency(output.risk_adjusted_avg_claim_amt_paid_by_ins_policy)}
                      </td>
                    ))}
                  </tr>
                  {/* v3b: Key Metrics additions */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Covered Lives
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {displayOriginal(output.covered_lives)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Average Annual Salary
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatCurrency(output.average_annual_salary)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Handbook Benefit Replacement Ratio
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.handbook_benefit_replacement_ratio)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      % Total Handbook Exposure Covered
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {formatPercentage(output.percent_total_exposure_covered)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-blueRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-blueCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Expected Claim Count
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-blueRow)' }}>
                        {displayOriginal(output.expected_claim_count)}
                      </td>
                    ))}
                  </tr>
                  {/* v3b col-J: Pre-Announced Claims rows moved to bottom of Rating Factors */}

                  {/* v3b: Rating Factors block */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td rowSpan={8} className="sticky left-0 z-30 border-r w-6 min-w-[1.5rem]" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-center justify-center h-full py-3">
                        <span className="text-sm font-bold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--color-text-primary)' }}>
                          Rating Factors
                        </span>
                      </div>
                    </td>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Tolerable Loss Ratio
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatPercentage(output.tolerable_loss_ratio)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Pricing Loss Ratio (Min Applied)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatPercentage(output.pricing_loss_ratio)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Pricing Loss Ratio (Min Applied, Comm Adjusted)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatPercentage(output.pricing_loss_ratio_with_adj)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Annual Claim Frequency Per 1000
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {displayOriginal(output.annual_claim_frequency_per_1000)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Volumes (WCB)
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {displayOriginal(output.volumes_wcb)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Rate Factor Version
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {output.rate_factor_version || '—'}
                      </td>
                    ))}
                  </tr>
                  {/* v3b col-J: Pre-Announced rows moved here from Key Metrics */}
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Pre-Announced Claims
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {displayOriginal(output.pre_announced_claims)}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-matrix-yellowLightRow)' }}>
                    <td className="sticky left-6 z-20 px-2 py-1.5 whitespace-nowrap text-sm font-medium border-r" style={{ backgroundColor: 'var(--color-matrix-yellowLightCell)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                      Pre-Announced Claim Ratio
                    </td>
                    {quote.outputs.map((output, idx) => (
                      <td key={idx} className="px-0 py-1.5 whitespace-nowrap text-sm text-center border-r" style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-secondary)', backgroundColor: output.is_sold_plan ? '#dcfce7' : 'var(--color-matrix-yellowLightRow)' }}>
                        {formatPercentage(output.pre_announced_claim_ratio)}
                      </td>
                    ))}
                  </tr>
                  </>
                  )}
                </tbody>
              </table>
              </div>
            </div>
            ) : (
              <div className="text-center py-8 px-6" style={{ color: 'var(--color-text-secondary)' }}>
                <p className="text-sm">No outputs created yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="rounded-lg border"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <button
          onClick={() => toggleSection('notes')}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Notes
          </h2>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {expandedSections.notes ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.notes && (
          <div className="px-6 pb-6 space-y-4">
            {/* Add Note Form */}
            <div className="space-y-2">
              <Textarea
                label="Add Note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={handleNoteKeyDown}
                placeholder="Enter your note here... (Press Enter to submit, Shift+Enter for new line)"
                rows={3}
                maxLength={1000}
                fullWidth
              />
              <div className="flex justify-between items-center">
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {noteText.length}/1000 characters
                </span>
                <button
                  onClick={handleAddNote}
                  disabled={!noteText.trim() || addingNote}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium text-white',
                    (!noteText.trim() || addingNote) && 'opacity-50 cursor-not-allowed'
                  )}
                  style={{ backgroundColor: 'var(--color-primary-500)' }}
                >
                  {addingNote ? 'Adding...' : 'Add Note'}
                </button>
              </div>
            </div>

            <div className="space-y-3 mt-6">
              {quote.notes && quote.notes.length > 0 ? (
                quote.notes.map((note) => (
                  <div
                    key={note.id}
                    className="p-4 rounded-lg border"
                    style={{ borderColor: 'var(--color-border-light)' }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {formatUser(note.created_by)}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {formatDateTime(note.created)}
                        </p>
                      </div>
                      {note.status && (
                        <Badge variant={getStatusVariant(note.status)} size="sm">
                          {QUOTE_STATUS_OPTIONS.find((opt) => opt.value === note.status)?.label || note.status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-primary)' }}>
                      {note.notes.replace(
                        /selected variations: (\d+(?:, \d+)*)/g,
                        (_match, variations) => {
                          const formatted = variations.split(', ').map((v: string) => `#${v.trim()}`).join(', ');
                          return `selected variations: ${formatted}`;
                        }
                      )}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-center py-4" style={{ color: 'var(--color-text-secondary)' }}>
                  No notes yet
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className="rounded-lg border"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <button
          onClick={() => toggleSection('metadata')}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Metadata
          </h2>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {expandedSections.metadata ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.metadata && (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Created
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {formatDateTime(quote.created)}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Created By
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {formatUser(quote.created_by)}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Last Modified
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {formatDateTime(quote.modified)}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Updated By
              </div>
              <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {formatUser(quote.updated_by)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {quote && !quote.is_analysis_file_generated && (
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={async () => {
              if (!quote || selectedVariations.size === 0) {
                setSuccessModal({
                  isOpen: true,
                  message: 'Please select at least one variation to include.',
                });
                return;
              }

              setIsProcessingNewVersion(true);
              try {
                await quotesService.generateAnalysisFile(
                  quote.id,
                  Array.from(selectedVariations)
                );
                startAnalysisPolling(quote.id, true);
              } catch (error: unknown) {
                setIsProcessingNewVersion(false);
                const errorMsg =
                  error && typeof error === 'object' && 'response' in error
                    ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
                    : undefined;
                setSuccessModal({
                  isOpen: true,
                  message: errorMsg || (error instanceof Error ? error.message : 'Failed to generate analysis file'),
                });
              }
            }}
            disabled={isProcessingNewVersion || selectedVariations.size === 0 || !quote.can_regenerate_analysis}
            title={!quote.can_regenerate_analysis ? 'This quote uses an outdated rater or analysis template — clone to a new version to regenerate.' : ''}
            className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: isProcessingNewVersion || selectedVariations.size === 0 || !quote.can_regenerate_analysis
                ? 'var(--color-neutral-400)'
                : 'var(--color-primary-500)'
            }}
          >
            {isProcessingNewVersion
              ? 'Generating...'
              : 'Generate Analysis & Proposal using Selected Variations'}
          </button>
        </div>
      )}

      {/* Processing Modal */}
      {isProcessingNewVersion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="rounded-lg border p-6 max-w-md w-full mx-4" style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}>
            <div className="text-center">
              <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"></div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Generating Analysis File
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Generating analysis file with selected variations. This may take a moment...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sold Plan Confirmation */}
      <ConfirmDialog
        isOpen={soldPlanConfirm !== null}
        title="Mark as Sold Plan"
        message={`Are you sure you want to mark Variation ${soldPlanConfirm?.variationIndex} as the sold plan? Any previously marked sold plan will be unset.`}
        confirmText={isMarkingSold ? 'Saving...' : 'Yes, Mark as Sold'}
        cancelText="Cancel"
        confirmVariant="primary"
        onConfirm={handleMarkAsSold}
        onCancel={() => setSoldPlanConfirm(null)}
      />

      {/* Success Modal */}
      <ConfirmDialog
        isOpen={successModal.isOpen}
        title={
          successModal.message.includes('successfully')
            ? 'Success'
            : successModal.message.includes('Please select')
              ? 'Action Required'
              : 'Notice'
        }
        message={successModal.message}
        confirmText="OK"
        showCancelButton={false}
        confirmVariant={
          successModal.message.includes('successfully')
            ? 'primary'
            : successModal.message.includes('Please select')
              ? 'warning'
              : 'danger'
        }
        onConfirm={() => {
          setSuccessModal({ isOpen: false, message: '' });
        }}
        onCancel={() => {
          setSuccessModal({ isOpen: false, message: '' });
        }}
      />
    </div>
  );
};

